import React, { FC, useEffect } from 'react';
import { Field, FieldInputProps, useField, useForm, useFormState } from 'react-final-form';
import { Box } from '@material-ui/core';
import Carousel from 'react-multi-carousel';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { Feature } from '../../util/featureFlags';
import Heading from './Heading';
import { ListingFlowFormField } from './ListingFlowTypes';
import {
  IconButton,
  InlineTextButton,
  ResponsiveImage,
  TypographyWrapper,
  ValidationError,
} from '../../components';
import { buildShopifyImageUrlToAltTextObject, getStockImages } from '../../util/shopifyHelpers';
import { useShopConfig } from '../../hooks/shopConfig';
import IconCloseFillCircle from '../../components/Icons/IconCloseFillCircle/IconCloseFillCircle';
import { useOwnListingVersions } from './hooks/useOwnListingVersions';
import { useIsMobile } from '../../hooks/useIsMobile';
import { defaultTreetStyles } from '../../shopConfig/config';
import { ListingItemType } from '../../types/sharetribe/listing';
import { TypographyFormat } from '../../components/TypographyWrapper/TypographyWrapper';
import { pluralize } from '../../util/strings';
import { trackChooseStockPhotos } from '../../util/heap';

interface StockPhotoForCarouselProps {
  photoUrl: string;
  onChange: () => void;
  altText?: string;
}

const StockPhotoForCarousel: FC<StockPhotoForCarouselProps> = (props) => {
  const { photoUrl, onChange, altText } = props;
  const alt =
    altText ||
    photoUrl?.split('/')?.pop()?.split('.')?.[0]?.replaceAll(/[-_]/g, ' ') ||
    'stock image';
  return (
    <Box
      mr={1}
      style={{
        position: 'relative',
        border: `2px solid ${defaultTreetStyles.gray10}`,
      }}
    >
      <Box style={{ position: 'absolute', top: '5px', right: '0' }} className="remove-stock-photo">
        <IconButton
          ariaLabel="Remove Photo"
          onClick={onChange}
          icon={<IconCloseFillCircle ariaHidden />}
          hoverIcon={
            <IconCloseFillCircle
              styleOverrides={{
                filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.5))',
                transform: 'scale(1.1)',
              }}
            />
          }
          ariaHidden
          tabIndex="-1"
        />
      </Box>
      <ResponsiveImage src={photoUrl} alt={alt} />
    </Box>
  );
};

interface StockPhotoCarouselProps {
  input: FieldInputProps<any, HTMLElement>;
  altTextReferenceObject?: Record<string, string>;
}

const StockPhotoCarousel: FC<StockPhotoCarouselProps> = (props) => {
  const { input, altTextReferenceObject } = props;
  const isMobile = useIsMobile();
  const deviceType = isMobile ? 'mobile' : 'desktop';

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 6,
      slidesToSlide: 2,
    },
    tablet: {
      breakpoint: { max: 1024, min: 550 },
      items: 3,
      slidesToSlide: 2,
    },
    mobile: {
      breakpoint: { max: 550, min: 0 },
      items: 3,
      slidesToSlide: 1,
    },
  };

  const baseIconStyle: any = {
    position: 'absolute',
    zIndex: 1,
    backgroundColor: defaultTreetStyles.gray60,
    opacity: 0.8,
    cursor: 'pointer',
    color: 'white',
  };

  return (
    <Carousel
      responsive={responsive}
      deviceType={deviceType}
      ssr
      swipeable
      partialVisible
      minimumTouchDrag={100}
      itemClass="choose-stock-image"
      customLeftArrow={<ChevronLeftIcon style={{ left: '0', ...baseIconStyle }} />}
      customRightArrow={<ChevronRightIcon style={{ right: '0', ...baseIconStyle }} />}
    >
      {input.value?.map((photoUrl: string) => {
        const handleRemovePhoto = () => {
          const newValue = input.value.filter((url: string) => url !== photoUrl);
          input.onChange(newValue);
        };

        const altText = altTextReferenceObject ? altTextReferenceObject[photoUrl] : undefined;
        return (
          <StockPhotoForCarousel
            onChange={handleRemovePhoto}
            photoUrl={photoUrl}
            key={photoUrl}
            altText={altText}
          />
        );
      })}
    </Carousel>
  );
};

const NoStockPhotosEmptyState = () => (
  <Box
    bgcolor={defaultTreetStyles.gray10}
    height="180px"
    display="flex"
    style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}
  >
    <Box m={1}>
      <TypographyWrapper variant="body1" typographyOverrides={{ style: { textAlign: 'center' } }}>
        All stock photos have been removed for this listing.
      </TypographyWrapper>
    </Box>
  </Box>
);

const ChooseStockPhotos = () => {
  const form = useForm();
  const { values, dirty } = useFormState();
  const { shopifyProductVariant, itemType, stockPhotos: stockPhotosFormValue } = values;
  const { currentListing } = useOwnListingVersions();
  const { shopifyProduct } = currentListing.attributes.publicData;
  const stockPhotoField = useField(ListingFlowFormField.StockPhotos);
  const { chooseStockImagesOption, maxStockImages } = useShopConfig();
  const canUserChooseStockPhotos = useFeatureFlags(Feature.ChooseStockPhotos);

  const allStockPhotos = getStockImages(
    shopifyProduct,
    shopifyProductVariant,
    chooseStockImagesOption
  );

  const altTextReferenceObject = buildShopifyImageUrlToAltTextObject(
    shopifyProduct,
    shopifyProductVariant
  );

  // If the derived stock photo urls are the same between shopify variant changes, then
  // we don't need to overwrite any edits to the stock photos.
  const sortedStockPhotos = [...allStockPhotos].sort().join(',');

  useEffect(() => {
    // Don't update stock photos on refresh,
    // but do update the form if the available set
    // of stock photos changes.
    if (dirty) {
      form.change(ListingFlowFormField.StockPhotos, allStockPhotos);
    }
  }, [sortedStockPhotos]);

  if (
    !canUserChooseStockPhotos ||
    !shopifyProduct ||
    maxStockImages === 0 ||
    itemType === ListingItemType.TradeIn ||
    allStockPhotos.length === 0
  ) {
    return null;
  }

  if (!stockPhotosFormValue) {
    form.change(ListingFlowFormField.StockPhotos, allStockPhotos);
  }

  const handleRestoreAllStockPhotos = () => {
    form.change(ListingFlowFormField.StockPhotos, allStockPhotos);
  };

  const numRemovedStockPhotos = stockPhotosFormValue
    ? allStockPhotos.length - stockPhotosFormValue.length
    : 0;

  const handleBeforeSubmit = () =>
    trackChooseStockPhotos(
      allStockPhotos.length,
      numRemovedStockPhotos,
      stockPhotosFormValue?.length
    );

  return (
    <>
      <Box mb={1} id={ListingFlowFormField.StockPhotos}>
        <Heading>Stock Photos</Heading>
        <ValidationError fieldMeta={stockPhotoField.meta} />
      </Box>
      <Box mb={1} position="relative">
        <TypographyWrapper variant="body1">
          Up to {maxStockImages} of the following stock photos are automatically attached to your
          listing. Remove any irrelevant images below.
        </TypographyWrapper>
        <Box width="100%" my={2}>
          <Field name={ListingFlowFormField.StockPhotos} beforeSubmit={handleBeforeSubmit}>
            {() =>
              stockPhotoField.input?.value?.length > 0 ? (
                <StockPhotoCarousel
                  input={stockPhotoField.input}
                  altTextReferenceObject={altTextReferenceObject}
                />
              ) : (
                <NoStockPhotosEmptyState />
              )
            }
          </Field>
        </Box>
        <Box position="absolute" bottom={-24}>
          <InlineTextButton
            onClick={handleRestoreAllStockPhotos}
            className="restore-remove-stock-photos"
            // Setting visibility to hidden instead of removing the component
            // as to not trigger a content shift on the form when the button
            // appears.
            style={numRemovedStockPhotos <= 0 && { visibility: 'hidden' }}
          >
            <TypographyWrapper
              component="span"
              variant="body2"
              format={TypographyFormat.Underlined}
              typographyOverrides={{ style: { fontWeight: 500 } }}
            >
              {`Restore ${numRemovedStockPhotos} removed stock ${pluralize(
                'photo',
                numRemovedStockPhotos
              )}`}
            </TypographyWrapper>
          </InlineTextButton>
        </Box>
      </Box>
    </>
  );
};

export default ChooseStockPhotos;
